import React, { useState, useRef, useEffect } from 'react';
import { Upload, Link, Trash2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface LogoInfo {
  data_uri: string;
  mime_type: string;
  width: number;
  height: number;
}

export function LogoUploader() {
  const [uploadMode, setUploadMode] = useState<'file' | 'url'>('file');
  const [isUploading, setIsUploading] = useState(false);
  const [currentLogo, setCurrentLogo] = useState<LogoInfo | null>(null);
  const [logoUrl, setLogoUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Fetch current logo on mount
  useEffect(() => {
    fetchCurrentLogo();
  }, []);

  const fetchCurrentLogo = async () => {
    try {
      const response = await fetch('/api/v1/settings/logo');
      if (response.ok) {
        const data = await response.json();
        if (data.logo) {
          setCurrentLogo(data.logo);
          setPreviewUrl(data.logo.data_uri);
        }
      }
    } catch (error) {
      console.error('Failed to fetch logo:', error);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPG, PNG, SVG, or WebP)');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    setIsUploading(true);

    try {
      const formData = new FormData();

      if (uploadMode === 'file') {
        const file = fileInputRef.current?.files?.[0];
        if (!file) {
          toast.error('Please select a file to upload');
          setIsUploading(false);
          return;
        }
        formData.append('file', file);
      } else {
        if (!logoUrl.trim()) {
          toast.error('Please enter a URL');
          setIsUploading(false);
          return;
        }
        formData.append('url', logoUrl);
      }

      const response = await fetch('/api/v1/settings/logo/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (data.logo) {
          setCurrentLogo(data.logo);
          setPreviewUrl(data.logo.data_uri);
          toast.success('Logo uploaded successfully');

          // Clear inputs
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          setLogoUrl('');

          // Trigger a global event to update the header
          window.dispatchEvent(new CustomEvent('logoUpdated', { detail: data.logo }));
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload logo');
      }
    } catch (error: any) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete the current logo?')) {
      return;
    }

    try {
      const response = await fetch('/api/v1/settings/logo', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        setCurrentLogo(null);
        setPreviewUrl(null);
        toast.success('Logo deleted successfully');

        // Trigger a global event to update the header
        window.dispatchEvent(new CustomEvent('logoDeleted'));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete logo');
      }
    } catch (error: any) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete logo');
    }
  };

  return (
    <div className="bg-white dark:bg-[#1a1f5c]/50 rounded-xl border border-gray-200 dark:border-[#5869ff]/20 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
          <ImageIcon className="h-5 w-5 text-[#5869ff]" />
          <span>Company Logo</span>
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Upload your company logo to display in the header. The logo will replace the search bar.
          Supported formats: JPG, PNG, SVG, WebP (max 5MB)
        </p>
      </div>
      <div className="space-y-6">
        {/* Current Logo Preview */}
        {previewUrl && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Current Logo</label>
            <div className="flex items-center space-x-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
                <img
                  src={previewUrl}
                  alt="Company logo"
                  className="max-h-16 max-w-[200px] object-contain"
                />
              </div>
              {currentLogo && (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {currentLogo.width && currentLogo.height && (
                    <p>Dimensions: {currentLogo.width} × {currentLogo.height}px</p>
                  )}
                  <p>Type: {currentLogo.mime_type}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Upload Mode Selection */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Upload Method</label>
          <div className="space-y-2">
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                value="file"
                checked={uploadMode === 'file'}
                onChange={() => setUploadMode('file')}
                className="w-4 h-4 text-[#5869ff] bg-white dark:bg-[#1a1f5c]/30 border-gray-300 dark:border-[#5869ff]/30"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Upload from computer</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                value="url"
                checked={uploadMode === 'url'}
                onChange={() => setUploadMode('url')}
                className="w-4 h-4 text-[#5869ff] bg-white dark:bg-[#1a1f5c]/30 border-gray-300 dark:border-[#5869ff]/30"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Download from URL</span>
            </label>
          </div>
        </div>

        {/* File Upload */}
        {uploadMode === 'file' && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="logo-file">Select Logo File</label>
            <div className="flex items-center space-x-2">
              <input
                id="logo-file"
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/svg+xml,image/webp"
                onChange={handleFileSelect}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:outline-none focus:border-[#5869ff]"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1f5c]/50 transition-colors"
              >
                <Upload className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* URL Input */}
        {uploadMode === 'url' && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="logo-url">Logo URL</label>
            <div className="flex items-center space-x-2">
              <input
                id="logo-url"
                type="url"
                placeholder="https://example.com/logo.png"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:outline-none focus:border-[#5869ff]"
              />
              <button
                className="p-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1f5c]/50 transition-colors"
              >
                <Link className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              The image will be downloaded and stored locally for security reasons.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={handleUpload}
              disabled={isUploading}
              className="px-4 py-2 bg-gradient-to-r from-[#5869ff] to-[#000742] text-white rounded-lg flex items-center gap-2 hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Upload Logo
                </>
              )}
            </button>
            {currentLogo && (
              <button
                onClick={handleDelete}
                className="px-4 py-2 border border-red-600 text-red-600 rounded-lg flex items-center gap-2 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Remove Logo
              </button>
            )}
          </div>

          {/* Help Text */}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Logo will be automatically resized to fit the header (max 300×60px)
          </p>
        </div>
      </div>
    </div>
  );
}